import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { createMapScanSession } from "../lib/createMapScanSession";
import "./ARView.css";
import "./MapScanPage.css";

const CONTAINER_ID = "map-scan-container";
const FOOTER_ID    = "map-scan-footer";

/** Overlay inicial de permiso (antes de arrancar la cámara) */
function ScanIntro({ onStart, onBack }) {
  return (
    <div className="mapscan-intro" role="dialog" aria-modal="true">
      <div className="mapscan-intro-inner">
        <div className="mapscan-icon" aria-hidden>🗺️</div>
        <h2 className="mapscan-title">Escanear mapa de Chile</h2>
        <p className="mapscan-text">
          Abre el libro en la página del mapa de volcanes y apunta la cámara
          al marcador. La app reconocerá el marcador y abrirá el mapa 3D
          interactivo.
        </p>
        <p className="mapscan-text mapscan-text-small">
          Solo usamos la cámara para reconocer el marcador. No se guardan
          fotos ni videos.
        </p>
        <button type="button" className="mapscan-btn-primary" onClick={onStart}>
          Activar cámara
        </button>
        <button type="button" className="mapscan-btn-ghost" onClick={onBack}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default function MapScanPage() {
  const navigate    = useNavigate();
  const sessionRef  = useRef(null);
  const [scanning, setScanning] = useState(false);

  /** Cuando MindAR detecta el marcador → parar cámara → ir a /mapa */
  const handleDetected = useCallback(() => {
    sessionRef.current?.dispose();
    sessionRef.current = null;
    navigate("/mapa");
  }, [navigate]);

  /** Arrancar la sesión MindAR (dentro del mismo gesto de usuario) */
  const startScan = useCallback(() => {
    setScanning(true);
    // rAF garantiza que el DOM ya pintó el contenedor
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = document.getElementById(CONTAINER_ID);
        const footer    = document.getElementById(FOOTER_ID);
        if (!container || !footer) return;

        const session = createMapScanSession({
          container,
          footer,
          onMapDetected: handleDetected,
        });
        sessionRef.current = session;
        void session.start();
      });
    });
  }, [handleDetected]);

  const handleBack = useCallback(() => {
    sessionRef.current?.dispose();
    sessionRef.current = null;
    navigate(-1);
  }, [navigate]);

  /** Limpiar cámara al desmontar */
  useEffect(() => {
    return () => {
      sessionRef.current?.dispose();
      sessionRef.current = null;
    };
  }, []);

  /* Chrome flotante (botón Volver) — portal a body para estar sobre el canvas WebGL */
  const chrome = createPortal(
    <div className="ar-ui-chrome">
      <button type="button" className="ar-back" onClick={handleBack}>
        ← Volver
      </button>
    </div>,
    document.body
  );

  return (
    <div className="ar-page-root">
      {/* Overlay de permiso solo antes de escanear */}
      {!scanning && <ScanIntro onStart={startScan} onBack={handleBack} />}

      {chrome}

      {/* Contenedor donde MindAR inyecta el video + canvas WebGL */}
      <div id={CONTAINER_ID} className="ar-mindar-container" />

      {/* Footer de estado */}
      <div id={FOOTER_ID} className={`ar-footer-text${scanning ? " show" : ""}`} />
    </div>
  );
}
